// This file still needs love
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {
	AttributeValue,
	emptyValueSymbol,
	useAttributeProvider,
	useMetadataLookups,
} from '@netx/core-attribute';
import type {Action, AttributeFormValues} from '@netx/core-attribute-asset';
import {
	AssetAttributeEditor,
	assetAttributeEditorClasses,
} from '@netx/core-attribute-asset/components/AssetAttributeEditor';
import {EditorProps as BulkEditorComponentProps} from '@netx/core-attribute-asset/components/Editor';
import AppendAction from '@netx/core-attribute-quick-edit/components/Actions/AppendToggle';
import {AttributeSetSelectionProvider} from '@netx/core-attribute-set-selection/context';
import {AttributeFormActionsProvider} from '@netx/core-attribute/context/AttributeFormActionsContext';
import {useAttributesStore} from '@netx/core-attribute/store/attributesStore';
import {parseValue} from '@netx/core-attribute/utils';
import {DrawerVariant} from '@netx/core-components/Drawer';
import {DrawerAnchor, withDrawer} from '@netx/core-components/Drawer';
import {
	FormMultipleValues,
	FormType,
	isEmptyValue,
} from '@netx/core-components/Form';
import {
	useUpdateEffect,
	useWhenChangedEffect,
} from '@netx/core-components/hooks';
// import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import type {Theme} from '@netx/core-components/Theme';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {i18n} from '@netx/core/i18n';
import {useGridStore, useProgressStore} from '../../store';

type SideEditorFormType = FormType<AttributeFormValues, Action>;

const AttributeQuickEditEditor = styled(AssetAttributeEditor)(({theme}) => ({
	zIndex: 1,
	// [`& .${assetAttributeEditorClasses.attributesWrapper}`]: {
	// 	padding: theme.spacing(0, 2),
	// },
	[`& .${assetAttributeEditorClasses.selector}`]: {
		padding: theme.spacing(1, 2),
	},
}));

/** Get submit data from attribute values */
// TODO: utilize the attribute selection context for attribute templates
function getSubmitData(
	values: SideEditorFormType['values'],
	changes: SideEditorFormType['changes'],
	actions: SideEditorFormType['actions'],
	mode?: Action,
) {
	const submitData: Record<string, any> = {}; // SideEditorFormType['values'] = {};
	Object.keys(values).forEach((key) => {
		const id = parseInt(key.toString());

		if (changes[id] !== true) return;

		const attr = useAttributesStore.getState().getAttributeById(id);
		if (!attr) return;

		const fieldAction = actions[id] || 'overwrite';
		const updateType =
			mode === 'append' && fieldAction !== 'clear' ? 'append' : fieldAction;
		const value = values[id];

		// Cannot empty a mandatory attribute
		const empty = isEmptyValue(value) || updateType === 'clear';
		if (empty && attr.mandatory === 1) return;

		submitData[attr.name] = value;
	});

	return submitData;
}

// We handle the submit from the form and pass it along to you, the condumer!
export interface BulkEditorProps extends Omit<
	BulkEditorComponentProps,
	| 'actions'
	| 'errors'
	| 'form'
	| 'inputForm'
	| 'values'
	| 'multipleValues'
	| 'originalValues'
	| 'onSubmit'
> {
	active?: boolean;
	onChange?: (hasChanges: boolean) => void;
	onSubmit: (
		values: SideEditorFormType['values'],
		actions: SideEditorFormType['actions'],
	) => Promise<void>;
}

const BulkEditor: React.FC<BulkEditorProps> = ({
	active,
	disabled,
	onChange,
	onSubmit,
	...props
}) => {
	const {state} = useGridStore();
	const progress = useProgressStore();
	const [working, setWorking] = useState(false);
	const attributeProvider = useAttributeProvider();

	const data = useMemo(() => {
		const values: AttributeFormValues = {};
		const multipleValues: FormMultipleValues = {};
		const readOnlyValues: Record<number, boolean> = {};

		const {storedData} = state;
		const keys = storedData ? Object.keys(storedData) : [];
		if (keys.length) {
			for (const key in storedData) {
				const data = storedData[key];
				const attr = attributeProvider.data.find((attr) => attr.name === key);
				if (attr) {
					const parsed = parseValue(attr.type, data.value) as AttributeValue;
					values[attr.attributeid] = data.multipleValues
						? emptyValueSymbol
						: Array.isArray(parsed)
							? parsed
							: [parsed];
					multipleValues[attr.attributeid] = data.multipleValues === true;
					readOnlyValues[attr.attributeid] = data.readOnly === true;
				}
			}
		}

		return {
			values,
			multipleValues,
			readOnlyValues,
		};
	}, [state.storedData, attributeProvider.data]);

	const form = useForm<AttributeFormValues>({});

	const ids = useMemo(() => {
		return Object.keys(data.values).map((id) => parseInt(id.toString()));
	}, [data.values]);

	const {lookup, lookupsAllowed, attributesWorking} = useMetadataLookups({
		attributeTemplates: attributeProvider.data,
		attributeIds: ids,
		onLookup: (values, attributeId, lookupValue) => {
			form.setValue(attributeId, lookupValue, {
				shouldDirty: true,
				shouldTouch: true,
				shouldValidate: true,
			});
		},
	});

	useWhenChangedEffect(
		form.formState.isDirty,
		useCallback(
			(hasChanged) => {
				onChange?.(hasChanged);
			},
			[onChange],
		),
	);

	// When `mode` changes, reset form to original values
	useUpdateEffect(() => {
		form.reset(
			form.formState.defaultValues,
			// undefined,
			// form.multipleValuesRef.current,
		);
	}, [props.mode]);

	useWhenChangedEffect(
		data,
		useCallback(
			(data) => {
				form.reset(data.values); //, undefined, data.multipleValues);
			},
			[form.reset],
		),
		{
			executeAtMount: true,
		},
	);

	useEffect(() => {
		const working = !active || progress.state.working; // || !Object.keys(data.values).length;
		if (working) {
			setWorking(true);
			return;
		}
		setTimeout(() => {
			setWorking(false);
		}, 200);
	}, [active, progress.state.working]);

	return (
		<AttributeSetSelectionProvider attributeIds={ids}>
			<AttributeFormActionsProvider
				form={form}
				onSubmit={async (data, actions) => {
					if (disabled) return;
					if (!Object.keys(data).length) return;

					const updatedValues = getSubmitData(
						data,
						form.formState.dirtyFields,
						actions,
						props.mode,
					);

					const updatedActions: SideEditorFormType['actions'] = {};
					// TODO: we are actually using the attribute names here and not the ids, so our form typing is not correct
					// we just need to clean this all up - we do submit with ids to get here but getSubmitData returns names instead
					// so we are incorrectly saying what comes out of here
					for (const key in updatedValues)
						updatedActions[key as any] = props.mode || actions[key as any];

					return await onSubmit(updatedValues, updatedActions);
				}}
			>
				{({handleSubmit, toggleActions, setAction}) => (
					<AttributeQuickEditEditor
						clearable
						appendable
						{...props}
						appending={props.mode === 'append'}
						attributesWorking={attributesWorking}
						hideSetSelector
						// disabled={disabled || saving || loading}
						form={form}
						hideReadOnlyAttributes
						// isLoading={loading}
						multipleValues={data.multipleValues}
						useActions
						working={working}
						onActionChange={setAction}
						onLookup={lookup}
						onSubmit={handleSubmit}
					/>
				)}
			</AttributeFormActionsProvider>
		</AttributeSetSelectionProvider>
	);

	// return (
	// 	<StyledBulkEditorComponent
	// 		appendable
	// 		clearable
	// 		attributesWorking={attributesWorking}
	// 		disabled={disabled}
	// 		// errors={form.errors}
	// 		form={form}
	// 		hideSetSelector
	// 		// inputForm={inputForm}
	// 		isLoading={working}
	// 		limitToValues
	// 		loading={working}
	// 		//actions={form.actions}
	// 		multipleValues={data.multipleValues}
	// 		onLookup={lookup}
	// 		lookupsAllowed={props.mode !== 'append' ? lookupsAllowed : undefined}
	// 		originalValues={data.values}
	// 		readOnlyAttributes={data.readOnlyValues}
	// 		// values={form.values}
	// 		{...props}
	// 		onSubmit={form.handleSubmit(async (values) => {
	// 			console.log('submit', values, form.formState.dirtyFields);
	// 		})}
	// 	/>
	// );
};

const BulkEditorDrawer = withDrawer(BulkEditor);

export default function ({
	active,
	...props
}: React.ComponentProps<typeof BulkEditorDrawer> & {active?: boolean}) {
	const [editorAppend, setEditorAppend] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	const [dataActive, setDataActive] = useState(false);

	const {getSelectedValues, resetStore, state} = useGridStore();

	const handleChange = useCallback(
		(hasChanges: boolean) => setHasChanges(hasChanges),
		[],
	);

	const handleEntered = useCallback(() => {
		if (!state.storedData) getSelectedValues();
		setDataActive(true);
	}, [state]);

	const handleExited = useCallback(() => {
		resetStore();
		setDataActive(false);
		setHasChanges(false);
	}, []);

	return useMemo(
		() => (
			<BulkEditorDrawer
				active={dataActive}
				appending={editorAppend}
				mode={editorAppend ? 'append' : 'overwrite'}
				submitLabel={
					editorAppend ? i18n.t('i18n.append') : i18n.t('i18n.apply')
				}
				onChange={handleChange}
				{...props}
				DrawerProps={{
					anchor: DrawerAnchor.RIGHT,
					cancelAsClose: true,
					headerActions: (
						<AppendAction
							confirm={hasChanges}
							onToggle={(event, on) => setEditorAppend(on)}
							on={editorAppend}
						/>
					),
					keepMounted: false,
					onEntered: handleEntered,
					onExited: handleExited,
					open: active,
					title: i18n.t('i18n.bulkEditor'),
					variant: DrawerVariant.TEMPORARY,
					...props.DrawerProps,
					width: 200,
				}}
			/>
		),
		[
			active,
			props,

			editorAppend,
			hasChanges,

			getSelectedValues,
			handleChange,
			resetStore,
		],
	);
}
